import {faFileDownload, faSearch, faUndo,} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import cn from "classnames";
import moment from "moment";
import React, {Fragment, useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {withTranslation} from "react-i18next";
import Select, {components} from "react-select";
import * as api from "../../../api/IrsClient";
import {
    ACTIVITY_OPTION,
    CHANNEL_OPTION,
    COUNTRY_OPTION,
    KEYCLOAK_ROLES,
    MAX_MONTH_RANGE,
    SEARCH_BY_OPTION,
    STATUS_OPTION,
    TRANS_STATUS_OPTION,
} from "../../../constant";
import DateRangePicker from "../../common/DateRangePicker";
import { monthRangeIsMoreThanMaxMonthRange } from "../../../utils/datetime";

const DATE_FORMAT = "dd/MM/yyyy HH:mm";
const SEARCH_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss";

function SearchForm({
  search,
  download,
  setErrorModal,
  exportable,
  loading,
  resetReport,
  initalDestinationCountry = 95,
  userRoles,
}) {
  const maxDate = moment().endOf("day").toDate();
  const [branch, setBranch] = useState([]);
  const [location, setLocation] = useState([]);
  const [isShownSearchValueField, setIsShownSearchValueField] = useState(false);
  const {
    register,
    unregister,
    handleSubmit,
    control,
    getValues,
    clearErrors,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      date: {
        from: null,
        to: null,
      },
      update_date: {
        from: null,
        to: null,
      },
      cashout_date: {
        from: null,
        to: null,
      },
      agent_id: [
        {
          value: "ALL",
          label: "All",
        },
      ],
      channel: [
        {
          value: "ALL",
          label: "All",
        },
      ],
      destination_country_code: initalDestinationCountry,
      destination_township: [
        {
          value: "ALL",
          label: "All",
        },
      ],
      trans_status: [
        {
          value: "ALL",
          label: "All",
        },
      ],
    },
  });

  useEffect(() => {
    init();
    getLocation();
  }, []);

  async function init() {
    try {
      let tmpBranchData = [];
      const {
        data: { data, status },
      } = await api.getBranch();
      if (status.code !== "0") {
        setErrorModal(true);
        return;
      }
      tmpBranchData = data.branch_list.map((x) => ({
        value: x.account,
        label: x.account,
      }));
      const branchValues = tmpBranchData;
      setBranch(branchValues);
    } catch (error) {
      setErrorModal(true);
      console.log(error);
    }
  }

  async function getLocation(country_code = initalDestinationCountry) {
    try {
      const {
        data: { status, data },
      } = await api.getLocation(country_code);
      if (status.code !== "0") {
        setErrorModal(true);
        return;
      }
      setLocation(
        data.location_info.map((x) => ({
          value: x.location_name_eng,
          label: x.location_name_eng,
        }))
      );
    } catch (error) {
      setErrorModal(true);
      console.log(error);
    }
  }

  function formatSearchReportInfo(values) {
    let body = {
      agent_id: values.agent_id
        ? values.agent_id.map((x) => x.value).filter((x) => x !== "ALL")
        : [],
      start_date: values.date.from
        ? moment(values.date.from).startOf("minutes").format(SEARCH_DATE_FORMAT)
        : "",
      end_date: values.date.to
        ? moment(values.date.to).endOf("minutes").format(SEARCH_DATE_FORMAT)
        : "",
      start_updated_date: values.update_date.from
        ? moment(values.update_date.from).startOf("minutes").format(SEARCH_DATE_FORMAT)
        : "",
      end_updated_date: values.update_date.to
        ? moment(values.update_date.to).endOf("minutes").format(SEARCH_DATE_FORMAT)
        : "",
      start_cashout_date: values.cashout_date.from
          ? moment(values.cashout_date.from).startOf("minutes").format(SEARCH_DATE_FORMAT)
          : "",
      end_cashout_date: values.cashout_date.to
          ? moment(values.cashout_date.to).endOf("minutes").format(SEARCH_DATE_FORMAT)
          : "",
      activity: values.activity,
      destination_country_code: values.destination_country_code,
      status: values.status,
      channel: values.channel
        ? values.channel.map((x) => x.value).filter((x) => x !== "ALL")
        : [],
      trans_status: values.trans_status
        ? values.trans_status.map((x) => x.value).filter((x) => x !== "ALL")
        : [],
      destination_township: values.destination_township
        ? values.destination_township
            .map((x) => x.value)
            .filter((x) => x !== "ALL")
        : [],
    };
    if (values.search_by) {
      body = { ...body, [values.search_by]: values.search_value };
    }
    return body;
  }

  async function onClickDownload(e) {
    e.preventDefault();
    const result = await trigger(["date.from", "date.to", "search_value"]);
    if (!result) return;
    const values = getValues();
    const body = formatSearchReportInfo(values);
    download(body);
  }

  function onSubmit(values) {
    const body = formatSearchReportInfo(values);
    search(body);
  }

  function onChangeDate(from, to) {
    setValue("date", {
      from,
      to,
    });
    clearErrors();
  }

  function onChangeUpdateDate(from, to) {
    setValue("update_date", {
      from,
      to,
    });
    clearErrors();
  }

  function onChangeCashoutDate(from, to) {
    setValue("cashout_date", {
      from,
      to,
    });
    clearErrors();
  }

  function onChangeSearchByOption(e) {
    const value = e.target.value;
    if (!value) {
      setValue("search_value", '');
      clearErrors("search_value");
      unregister("search_value");
    }
    setIsShownSearchValueField(value);
  }

  function onChangeDestination(e) {
    const value = e.currentTarget.value;
    setValue("destination_township", [
      {
        value: "ALL",
        label: "All",
      },
    ]);
    if (!value) {
      setLocation([]);
      return;
    }
    getLocation(value);
  }

  function onClickClear() {
    reset();
    resetReport();
    setIsShownSearchValueField(false);
    getLocation();
  }

  function validateDateRange(from, to) {
    if (monthRangeIsMoreThanMaxMonthRange(from, to)) {
      return "Date range must not be more than " + MAX_MONTH_RANGE + " months";
    }
    return true;
  }

  function isRequiredDate(fieldName) {
    let isRequired = false;
    const values = getValues();
    switch (fieldName) {
      case "date.from":
        isRequired = !!(
          values.date.to ||
          (!values.update_date.from && !values.update_date.to && !values.cashout_date.from && !values.cashout_date.to)
        );
        break;
      case "date.to":
        isRequired = !!(
          values.date.from ||
          (!values.update_date.from && !values.update_date.to && !values.cashout_date.from && !values.cashout_date.to)
        );
        break;
      case "update_date.from":
        isRequired = !!(
          values.update_date.to ||
          (!values.date.from && !values.date.to && !values.cashout_date.from && !values.cashout_date.to)
        );
        break;
      case "update_date.to":
        isRequired = !!(
          values.update_date.from ||
          (!values.date.from && !values.date.to && !values.cashout_date.from && !values.cashout_date.to)
        );
        break;
      case "cashout_date.from":
        isRequired = !!(
            values.cashout_date.to ||
            (!values.update_date.from && !values.update_date.to && !values.date.from && !values.date.to)
        );
        break;
      case "cashout_date.to":
        isRequired = !!(
            values.cashout_date.from ||
            (!values.update_date.from && !values.update_date.to && !values.date.from && !values.date.to)
        );
        break;
      default:
        isRequired = false;
    }
    return isRequired;
  }

  function onValidateReceiver(value){
    const selectedReceiverId = getValues("search_by");
    if(selectedReceiverId==="receiver_id" && value.length < 6){
        return "Receiver ID must not be less than 6 characters";
    }
    return true;
  }

  function MultiValue({ children, ...props }) {
    const selectedBranch = getValues(props.selectProps.name);
    if (!selectedBranch) {
      return null;
    }
    const x = selectedBranch.find((x) => x.value === "ALL");
    if (x) {
      if (children === "All") {
        return (
          <components.MultiValue {...props}>{children}</components.MultiValue>
        );
      } else {
        return null;
      }
    }
    return <components.MultiValue {...props}>{children}</components.MultiValue>;
  }

  const reactSelectCustomStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#80bdff" : "hsl(0,0%,80%)",
      outline: "0",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "unset",
    }),
    valueContainer: (provided) => ({
      ...provided,
    }),
    multiValue: (provided, state) => {
      return state.data.value === "ALL"
        ? { ...provided, paddingRight: "6px" }
        : provided;
    },
    multiValueRemove: (provided, state) => {
      return state.data.value === "ALL"
        ? { ...provided, display: "none" }
        : provided;
    },
  };

  function handleSelectChange(key, values, newValue) {
    if (newValue.option && newValue.option.value === "ALL") {
      setValue(key, [newValue.option]);
    } else {
      if (!values || values.length === 0) {
        setValue(key, [
          {
            value: "ALL",
            label: "All",
          },
        ]);
      } else {
        setValue(
          key,
          values.filter((x) => x.value !== "ALL")
        );
      }
    }
  }

  function filterCountryOption() {
    const allOption = [{ label: "Select All", value: "" }, ...COUNTRY_OPTION];
    if (
      userRoles.includes(KEYCLOAK_ROLES.VIEW_IRS_MYANMAR_TRANSACTION_RPT) &&
      userRoles.includes(KEYCLOAK_ROLES.VIEW_IRS_CAMBODIA_TRANSACTION_RPT)
    ) {
      return allOption;
    } else if (
      userRoles.includes(KEYCLOAK_ROLES.VIEW_IRS_MYANMAR_TRANSACTION_RPT)
    ) {
      return COUNTRY_OPTION.filter((item) => item.value === "95");
    } else if (
      userRoles.includes(KEYCLOAK_ROLES.VIEW_IRS_CAMBODIA_TRANSACTION_RPT)
    ) {
      return COUNTRY_OPTION.filter((item) => item.value === "855");
    }
    return allOption;
  }

  watch(["date", "update_date", "cashout_date"]);

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" data-testid="search-report-from" >
        <div className="card-body form-search form-horizontal">
          <div className="row">
            <div className="col-sm-3">
              <label>Transaction Create Date</label>
              <DateRangePicker
                control={control}
                prefixFieldName="date"
                startDate={getValues("date.from")}
                endDate={getValues("date.to")}
                showTime
                dateFormat={DATE_FORMAT}
                maxDate={maxDate}
                onChange={onChangeDate}
                errors={errors}
                rules={{
                  from: {
                    required: {
                      value: isRequiredDate("date.from"),
                      message: `Please input date from`,
                    },
                    validate: {
                      range: (v) => validateDateRange(v, getValues("date.to")),
                    },
                  },
                  to: {
                    required: {
                      value: isRequiredDate("date.to"),
                      message: `Please input date to`,
                    },
                  },
                }}
              />
            </div>
            <div className="col-sm-3">
              <label>Transaction Update Date</label>
              <DateRangePicker
                control={control}
                prefixFieldName="update_date"
                startDate={getValues("update_date.from")}
                endDate={getValues("update_date.to")}
                showTime
                dateFormat={DATE_FORMAT}
                maxDate={maxDate}
                onChange={onChangeUpdateDate}
                errors={errors}
                rules={{
                  from: {
                    required: {
                      value: isRequiredDate("update_date.from"),
                      message: `Please input date from`,
                    },
                    validate: {
                      range: (v) =>
                        validateDateRange(v, getValues("update_date.to")),
                    },
                  },
                  to: {
                    required: {
                      value: isRequiredDate("update_date.to"),
                      message: `Please input date to`,
                    },
                  },
                }}
              />
            </div>
            <div className="col-sm-3">
              <label>Cashout Date</label>
              <DateRangePicker
                  control={control}
                  prefixFieldName="cashout_date"
                  startDate={getValues("cashout_date.from")}
                  endDate={getValues("cashout_date.to")}
                  showTime
                  dateFormat={DATE_FORMAT}
                  maxDate={maxDate}
                  onChange={onChangeCashoutDate}
                  errors={errors}
                  rules={{
                    from: {
                      required: {
                        value: isRequiredDate("cashout_date.from"),
                        message: `Please input date from`,
                      },
                      validate: {
                        range: (v) =>
                            validateDateRange(v, getValues("cashout_date.to")),
                      },
                    },
                    to: {
                      required: {
                        value: isRequiredDate("cashout_date.to"),
                        message: `Please input date to`,
                      },
                    },
                  }}
              />
            </div>
            <div className="col-sm-3">
              <label htmlFor="branch-report" >Branch</label>
              <Controller
                render={({ field }) => (
                  <Select
                    inputId="branch-report"
                    {...field}
                    isMulti={true}
                    closeMenuOnSelect={false}
                    isSearchable
                    options={[{ value: "ALL", label: "All" }, ...branch]}
                    hideSelectedOptions={false}
                    styles={reactSelectCustomStyles}
                    components={{ MultiValue }}
                    onChange={(values, newValue) =>
                      handleSelectChange("agent_id", values, newValue)
                    }
                  />
                )}
                name="agent_id"
                control={control}
                defaultValue={[
                  {
                    value: "ALL",
                    label: "All",
                  },
                ]}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3">
              <label>Channel</label>
              <Controller
                  render={({ field }) => (
                      <Select
                          {...field}
                          isMulti={true}
                          closeMenuOnSelect={false}
                          isSearchable
                          options={[
                            { value: "ALL", label: "All" },
                            ...CHANNEL_OPTION,
                          ]}
                          hideSelectedOptions={false}
                          styles={reactSelectCustomStyles}
                          components={{ MultiValue }}
                          onChange={(values, newValue) =>
                              handleSelectChange("channel", values, newValue)
                          }
                      />
                  )}
                  name="channel"
                  control={control}
                  defaultValue={[
                    {
                      value: "ALL",
                      label: "All",
                    },
                  ]}
              />
            </div>
            <div className="col-sm-3">
              <label>Status</label>
              <select className="form-control" {...register("status")}>
                <option value="">Select All</option>
                {STATUS_OPTION.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-3">
              <label>Transaction status</label>
              <Controller
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti={true}
                    closeMenuOnSelect={false}
                    isSearchable
                    options={[
                      { value: "ALL", label: "All" },
                      ...TRANS_STATUS_OPTION,
                    ]}
                    hideSelectedOptions={false}
                    styles={reactSelectCustomStyles}
                    components={{ MultiValue }}
                    onChange={(values, newValue) =>
                      handleSelectChange("trans_status", values, newValue)
                    }
                  />
                )}
                name="trans_status"
                control={control}
                defaultValue={[
                  {
                    value: "ALL",
                    label: "All",
                  },
                ]}
              />
            </div>
            <div className="col-sm-3">
              <label>Destination Country</label>
              <select
                className="form-control"
                {...register("destination_country_code")}
                onChange={onChangeDestination}
              >
                {filterCountryOption().map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-3">
              <label>Destination Township/City</label>
              <Controller
                  render={({ field }) => (
                      <Select
                          {...field}
                          isLoading={loading}
                          isMulti={true}
                          closeMenuOnSelect={false}
                          isSearchable
                          options={[{ value: "ALL", label: "All" }, ...location]}
                          hideSelectedOptions={false}
                          styles={reactSelectCustomStyles}
                          components={{ MultiValue }}
                          isDisabled={location.length <= 0}
                          onChange={(values, newValue) =>
                              handleSelectChange(
                                  "destination_township",
                                  values,
                                  newValue
                              )
                          }
                      />
                  )}
                  name="destination_township"
                  control={control}
                  defaultValue={[
                    {
                      value: "ALL",
                      label: "All",
                    },
                  ]}
              />
            </div>
            <div className="col-sm-3">
              <label>Activity</label>
              <select className="form-control" {...register("activity")}>
                <option value="">Select All</option>
                {ACTIVITY_OPTION.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-sm-3">
              <label>Search By</label>
              <select
                className="form-control"
                {...register("search_by")}
                onChange={onChangeSearchByOption}
              >
                <option value="">Please Select</option>
                {SEARCH_BY_OPTION.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
            <div
              className={cn("col-sm-3", {
                "form-error": errors?.search_value,
              })}

            >
              <label>Please specify</label>
              <input
                disabled={!isShownSearchValueField}
                className="form-control"
                {...register("search_value",
                    { required: isShownSearchValueField && "Please specify value",
                    validate: {onValidateReceiver}})}
              />
              {errors?.search_value && (
                <span className="input-error-message">
                  {errors.search_value.message}
                </span>
              )}
            </div>
          </div>
          <div className="row mt-4">
            <div className="offset-sm-3 col-sm-3 text-lg-right">
              <button
                className="btn btn-warning"
                type="submit"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSearch} /> Search
              </button>
            </div>
            <div className="col-sm-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClickClear}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faUndo} /> Clear
              </button>
            </div>
            <div className="col-sm-3">
              <button
                type="button"
                className="btn btn-success"
                style={{ float: "right" }}
                onClick={onClickDownload}
                hidden={!exportable}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faFileDownload} /> Export
              </button>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
}

export default withTranslation()(SearchForm);
